import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ArrowRight, User, Trash2, TrendingUp, AlertTriangle, Phone, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { useSortableTable } from "@/hooks/useSortableTable";
import { EditableCell } from "@/components/EditableCell";
import { AIContentRenderer } from "@/components/AIContentRenderer";
import { PageControls } from "@/components/PageControls";

const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active', className: 'bg-green-100 text-green-700' },
  { value: 'Inactive', label: 'Inactive', className: 'bg-gray-100 text-gray-700' },
  { value: 'OnHold', label: 'On Hold', className: 'bg-yellow-100 text-yellow-700' },
  { value: 'VIP', label: 'VIP', className: 'bg-purple-100 text-purple-700' },
];

// Suggested rebook date pill — past dates muted, today/this-week highlighted,
// future > 7 days shown plainly. `hasNext` dims the cell when the client
// already has a future booking (no nudge needed).
function RebookCell({ date, hasNext, testid }) {
  if (!date) return <span className="text-xs text-muted-foreground" data-testid={testid}>—</span>;
  const d = new Date(date);
  if (isNaN(d.getTime())) return <span className="text-xs text-muted-foreground" data-testid={testid}>—</span>;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(d);
  target.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target - today) / (1000 * 60 * 60 * 24));
  const label = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

  let tone = 'text-foreground/70';
  let suffix = '';
  if (hasNext) {
    tone = 'text-muted-foreground line-through';
  } else if (diffDays < 0) {
    tone = 'text-red-500 font-medium';
    suffix = ` · ${-diffDays}d overdue`;
  } else if (diffDays === 0) {
    tone = 'text-primary font-semibold';
    suffix = ' · today';
  } else if (diffDays <= 7) {
    tone = 'text-amber-700 font-medium';
    suffix = ` · in ${diffDays}d`;
  }

  return (
    <span className={`text-xs ${tone}`} data-testid={testid} title={hasNext ? 'Already rebooked' : `Suggested rebook date${suffix}`}>
      {label}{suffix && <span className="text-[10px] opacity-80">{suffix}</span>}
    </span>
  );
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [retentionData, setRetentionData] = useState(null);
  const [loadingRetention, setLoadingRetention] = useState(false);

  const load = async () => {
    try {
      const params = new URLSearchParams({ limit: String(pageSize), skip: String((page - 1) * pageSize) });
      if (search) params.append('search', search);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const res = await axios.get(`${API}/clients?${params}`);
      setClients(res.data.clients || []);
      setTotal(res.data.total || 0);
    } catch (e) {  }
  };

  useEffect(() => { setPage(1); }, [statusFilter, search]);
  useEffect(() => { load(); }, [statusFilter, page, pageSize]);
  useEffect(() => { const t = setTimeout(() => { setPage(1); load(); }, 300); return () => clearTimeout(t); }, [search]);

  const { sortedData, SortHeader } = useSortableTable(clients, 'name');

  const saveField = async (clientId, field, value) => {
    try {
      await axios.put(`${API}/clients/${clientId}`, { [field]: value });
      // Update local state
      setClients(prev => prev.map(c => c.id === clientId ? { ...c, [field]: value } : c));
      toast.success('Updated');
    } catch (e) { toast.error('Failed to save'); }
  };

  return (
    <div className="space-y-4 fade-in">
      <Toaster position="top-center" />
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display' }}>Clients</h1>
          <p className="text-sm text-muted-foreground">{total} total clients · Click any cell to edit inline</p>
        </div>
      </div>

      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, email, or phone" className="pl-9 rounded-xl" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36 rounded-xl"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Beyond AI · client intelligence */}
      <Card className="p-4 rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)]" style={{ borderColor: '#BFD8F5' }}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <img src="https://customer-assets.emergentagent.com/job_a2ffd257-50b9-4530-8932-b0e03417ee6f/artifacts/9v3936lg_Beyond%20AI.png" alt="Beyond AI" className="w-5 h-5 rounded-lg object-cover" />
            <h3 className="text-sm font-semibold" style={{ fontFamily: 'Playfair Display' }}>
              Beyond <span style={{ color: '#2DD4BF' }}>AI</span> <span className="font-normal text-muted-foreground">· client intelligence</span>
            </h3>
          </div>
          <Button 
            size="sm" className="rounded-xl text-xs h-7 text-white border-0"
            style={{ background: 'linear-gradient(135deg,#1F6FE5 0%,#2DD4BF 100%)' }}
            onClick={async () => {
              setLoadingRetention(true);
              try {
                const res = await axios.get(`${API}/ai/retention-insights`);
                setRetentionData(res.data);
              } catch (e) {  }
              setLoadingRetention(false);
            }}
            disabled={loadingRetention}
          >
            {loadingRetention ? 'Beyond AI is thinking…' : retentionData ? 'Refresh' : 'Ask Beyond AI'}
          </Button>
        </div>
        {retentionData ? (
          <div className="fade-in">
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="p-2.5 bg-green-50 rounded-xl border border-green-100 text-center">
                <p className="text-lg font-semibold text-green-700 tabular-nums" style={{ fontFamily: 'Playfair Display' }}>{retentionData.data?.total_active_clients || 0}</p>
                <p className="text-[10px] text-green-600">Active Clients</p>
              </div>
              <div className="p-2.5 bg-orange-50 rounded-xl border border-orange-100 text-center">
                <p className="text-lg font-semibold text-orange-700 tabular-nums" style={{ fontFamily: 'Playfair Display' }}>{retentionData.data?.at_risk_clients?.length || 0}</p>
                <p className="text-[10px] text-orange-600">At Risk (21+ days)</p>
              </div>
              <div className="p-2.5 bg-red-50 rounded-xl border border-red-100 text-center">
                <p className="text-lg font-semibold text-red-700 tabular-nums" style={{ fontFamily: 'Playfair Display' }}>{retentionData.data?.lapsed_clients?.length || 0}</p>
                <p className="text-[10px] text-red-600">Lapsed (28+ days)</p>
              </div>
            </div>
            {(retentionData.data?.at_risk_clients || []).length > 0 && (
              <div className="p-2.5 bg-orange-50/50 rounded-xl border border-orange-100 mb-3">
                <p className="text-xs font-semibold text-orange-700 mb-1.5 flex items-center gap-1"><AlertTriangle size={12} /> Clients needing attention</p>
                <div className="space-y-1">
                  {retentionData.data.at_risk_clients.slice(0, 5).map((c, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <span className="font-medium">{c.name}</span>
                      <span className="text-orange-600">{c.days_since}d ago</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {retentionData.insights && (
              <details className="group">
                <summary className="text-xs font-medium cursor-pointer hover:underline" style={{ color: '#1F6FE5' }}>View Beyond AI analysis</summary>
                <div className="mt-3 max-h-[400px] overflow-y-auto">
                  <AIContentRenderer content={retentionData.insights} compact={true} />
                </div>
              </details>
            )}
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Ask Beyond AI for retention insights and growth opportunities.</p>
        )}
      </Card>

      <Card className="rounded-2xl border shadow-[0_10px_30px_rgba(43,18,27,0.06)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <SortHeader field="name">Name</SortHeader>
                <th className="text-left p-3 font-medium text-muted-foreground">Email</th>
                <th className="text-left p-3 font-medium text-muted-foreground">Phone</th>
                <SortHeader field="total_appointments">Appts</SortHeader>
                <SortHeader field="total_spent">Spent</SortHeader>
                <SortHeader field="days_since">Last Visit</SortHeader>
                <SortHeader field="next_appointment">Next Visit</SortHeader>
                <SortHeader field="three_week_date">3wk Rebook</SortHeader>
                <SortHeader field="four_week_date">4wk Rebook</SortHeader>
                <SortHeader field="status">Status</SortHeader>
                <th className="text-left p-3 font-medium text-muted-foreground">Notes</th>
                <th className="text-left p-3 font-medium text-muted-foreground w-20"></th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map(client => (
                <tr key={client.id} className="border-b border-border/50 hover:bg-muted/10 transition-colors">
                  <td className="p-1.5 pl-3">
                    <div className="flex items-center gap-1.5">
                      <EditableCell
                        value={client.name}
                        field="name"
                        onSave={(f, v) => saveField(client.id, f, v)}
                        className="font-medium"
                      />
                      {client.cash_only && (
                        <span title="Cash only — no deposit required" data-testid={`cash-only-badge-${client.id}`} className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 shrink-0">💵</span>
                      )}
                    </div>
                  </td>
                  <td className="p-1.5">
                    <EditableCell 
                      value={client.email} 
                      field="email"
                      onSave={(f, v) => saveField(client.id, f, v)}
                      className="text-xs"
                      placeholder="email"
                    />
                  </td>
                  <td className="p-1.5">
                    <EditableCell 
                      value={client.phone} 
                      field="phone"
                      onSave={(f, v) => saveField(client.id, f, v)}
                      className="text-xs"
                      placeholder="phone"
                    />
                  </td>
                  <td className="p-1.5 tabular-nums text-center">{client.total_appointments || 0}</td>
                  <td className="p-1.5 tabular-nums">£{(client.total_spent || 0).toFixed(2)}</td>
                  <td className="p-1.5">
                    {client.last_appointment ? (
                      <div className="leading-tight">
                        <div className="text-xs whitespace-nowrap" data-testid={`last-visit-date-${client.id}`}>
                          {new Date(client.last_appointment).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </div>
                        {client.days_since !== null && client.days_since !== undefined && (
                          <div className={`text-[10px] ${client.days_since > 28 ? 'text-red-500 font-medium' : 'text-muted-foreground'}`} data-testid={`days-since-${client.id}`}>
                            {client.days_since === 0 ? 'today' : `${client.days_since}d ago`}
                          </div>
                        )}
                      </div>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="p-1.5">
                    {client.next_appointment ? (
                      <span className="text-xs text-emerald-600 font-medium">
                        {new Date(client.next_appointment).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                      </span>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="p-1.5">
                    <RebookCell date={client.three_week_date} hasNext={!!client.next_appointment} testid={`three-week-${client.id}`} />
                  </td>
                  <td className="p-1.5">
                    <RebookCell date={client.four_week_date} hasNext={!!client.next_appointment} testid={`four-week-${client.id}`} />
                  </td>
                  <td className="p-1.5">
                    <EditableCell 
                      value={client.status}
                      field="status"
                      options={STATUS_OPTIONS}
                      onSave={(f, v) => saveField(client.id, f, v)}
                    />
                  </td>
                  <td className="p-1.5 max-w-[180px]">
                    <EditableCell 
                      value={client.notes} 
                      field="notes"
                      onSave={(f, v) => saveField(client.id, f, v)}
                      className="text-xs"
                      placeholder="Add note..."
                    />
                  </td>
                  <td className="p-1.5">
                    <div className="flex gap-0.5">
                      <Link to={`/admin/clients/${client.id}`}>
                        <Button variant="ghost" size="icon" className="h-6 w-6 rounded-lg"><ArrowRight size={12} /></Button>
                      </Link>
                      <Button variant="ghost" size="icon" onClick={async () => {
                        if (!window.confirm(`Delete ${client.name}?`)) return;
                        try { await axios.delete(`${API}/clients/${client.id}`); toast.success('Deleted'); load(); } catch (e) { toast.error('Failed'); }
                      }} className="h-6 w-6 rounded-lg text-destructive"><Trash2 size={11} /></Button>
                    </div>
                  </td>
                </tr>
              ))}
              {sortedData.length === 0 && <tr><td colSpan="12" className="p-8 text-center text-muted-foreground">No clients found</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
      <PageControls page={page} pageSize={pageSize} total={total} onPage={setPage} onPageSize={setPageSize} />
    </div>
  );
}
